import type { ProjectDtoOutput } from '@batiflow/api-client';
import { DataTable, DateText, EmptyState, isolate, type DataTableProps } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  ToggleButton,
  __css,
  tokens,
} from '@fluentui/react-components';
import { DocumentCheckmark24Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerPath } from '../document-details';
import { ApprovalStatusBadge } from './approval-badges';
import { useSpaceFlows } from './approvals-api';
import {
  FLOW_STATUSES,
  canDecide,
  documentLabel,
  stepProgress,
  type ApprovalFlow,
  type ApprovalFlowStatus,
} from './approvals-model';
import { DecisionDialog } from './decision-dialog';
import { FlowCard } from './flow-card';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"chips":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"code":{"Huce71":"fz5stix","Bahqtrf":"f1fedwem"},"title":{"Btd35i7":"fjksvth","Bhrd7zp":"fl43uef"},"documentCell":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"muted":{"sj55zd":"f11d4kpn"},"dates":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r"}});

type Columns = DataTableProps<ApprovalFlow>['columns'];
type StatusFilter = ApprovalFlowStatus | 'all';
const STATUS_CHIPS: readonly StatusFilter[] = ['all', ...FLOW_STATUSES];

interface DocumentLinkProps {
  orgId: string;
  projectId: string;
  flow: ApprovalFlow;
}

/** The document's code and title, linking to the viewer (a plain click stays in the app). */
function DocumentLink({ orgId, projectId, flow }: DocumentLinkProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    void navigate({
      to: '/o/$orgId/projects/$projectId/documents/$documentId',
      params: { orgId, projectId, documentId: flow.documentId },
    });
  };
  return (
    <span className={styles.documentCell}>
      <Link href={viewerPath(orgId, projectId, flow.documentId)} onClick={open}>
        <bdi className={styles.title}>{flow.documentTitle}</bdi>
      </Link>
      {flow.documentCode ? (
        <bdi dir="ltr" className={styles.code}>
          {flow.documentCode}
        </bdi>
      ) : null}
    </span>
  );
}

export interface ApprovalsTabProps {
  orgId: string;
  /** The project: its internal space holds the documents and their approval flows. */
  project: Pick<ProjectDtoOutput, 'id' | 'internalSpaceId'>;
}

/**
 * The Approvals tab of a project: the approval flows of its documents with status chips and a
 * "To decide by me" chip, each row with the document (a link to the viewer), index, status,
 * current step, who asked, the dates and the actions the caller has.
 */
export function ApprovalsTab({ orgId, project }: ApprovalsTabProps) {
  const { t } = useTranslation('documents');
  if (!project.internalSpaceId) {
    return (
      <EmptyState
        illustration={<DocumentCheckmark24Regular />}
        title={t('approvals.tab.noSpace.title')}
        description={t('approvals.tab.noSpace.description')}
      />
    );
  }
  return <ApprovalsList orgId={orgId} projectId={project.id} spaceId={project.internalSpaceId} />;
}

interface ListProps {
  orgId: string;
  projectId: string;
  spaceId: string;
}

function ApprovalsList({ orgId, projectId, spaceId }: ListProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [toDecide, setToDecide] = useState(false);
  const [open, setOpen] = useState<{ id: string; kind: 'decide' | 'details' } | null>(null);
  const flows = useSpaceFlows(spaceId, {
    ...(status !== 'all' ? { status } : {}),
    ...(toDecide ? { toDecide: 'true' as const } : {}),
  });
  const items = useMemo(() => flows.data?.items ?? [], [flows.data]);
  const selected = open ? items.find((flow) => flow.id === open.id) : undefined;
  const close = () => {
    setOpen(null);
  };

  const columns = useMemo<Columns>(
    () => [
      {
        id: 'document',
        header: t('approvals.tab.columns.document'),
        meta: { minWidth: 240 },
        cell: ({ row }) => <DocumentLink orgId={orgId} projectId={projectId} flow={row.original} />,
      },
      {
        id: 'index',
        header: t('approvals.tab.columns.index'),
        meta: { minWidth: 70 },
        cell: ({ row }) =>
          row.original.revisionIndex ? (
            <bdi dir="ltr">{row.original.revisionIndex}</bdi>
          ) : (
            <span className={styles.muted}>{t('list.none')}</span>
          ),
      },
      {
        id: 'status',
        header: t('approvals.tab.columns.status'),
        meta: { minWidth: 170 },
        cell: ({ row }) => <ApprovalStatusBadge status={row.original.status} />,
      },
      {
        id: 'step',
        header: t('approvals.tab.columns.step'),
        meta: { minWidth: 110 },
        cell: ({ row }) => {
          if (row.original.status !== 'in_progress')
            return <span className={styles.muted}>{t('list.none')}</span>;
          const { current, total } = stepProgress(row.original);
          return t('approvals.tab.stepOf', { current, total });
        },
      },
      {
        id: 'initiator',
        header: t('approvals.tab.columns.initiator'),
        meta: { minWidth: 140 },
        cell: ({ row }) =>
          row.original.startedByName ? (
            <bdi>{row.original.startedByName}</bdi>
          ) : (
            <span className={styles.muted}>{t('list.none')}</span>
          ),
      },
      {
        id: 'dates',
        header: t('approvals.tab.columns.dates'),
        meta: { minWidth: 170 },
        cell: ({ row }) => (
          <span className={styles.dates}>
            <span>
              {t('approvals.tab.started')} <DateText value={row.original.createdAt} />
            </span>
            {row.original.completedAt ? (
              <span>
                {t('approvals.tab.completed')} <DateText value={row.original.completedAt} />
              </span>
            ) : row.original.dueOn ? (
              <span>
                {t('approvals.tab.due')} <DateText value={row.original.dueOn} />
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'actions',
        header: t('approvals.tab.columns.actions'),
        meta: { minWidth: 200 },
        cell: ({ row }) => {
          const flow = row.original;
          const label = isolate(documentLabel(flow.documentCode, flow.documentTitle));
          return (
            <span className={styles.actions}>
              {canDecide(flow) ? (
                <Button
                  size="small"
                  appearance="primary"
                  aria-label={t('approvals.flow.decideFor', { document: label })}
                  onClick={() => {
                    setOpen({ id: flow.id, kind: 'decide' });
                  }}
                >
                  {t('approvals.flow.decide')}
                </Button>
              ) : null}
              <Button
                size="small"
                aria-label={t('approvals.tab.detailsFor', { document: label })}
                onClick={() => {
                  setOpen({ id: flow.id, kind: 'details' });
                }}
              >
                {t('approvals.tab.details')}
              </Button>
            </span>
          );
        },
      },
    ],
    [t, styles, orgId, projectId],
  );

  const emptyKey = toDecide ? 'toDecide' : status === 'all' ? 'none' : 'filtered';

  return (
    <div className={styles.root}>
      <div className={styles.chips} role="group" aria-label={t('approvals.tab.filtersLabel')}>
        {STATUS_CHIPS.map((value) => (
          <ToggleButton
            key={value}
            size="small"
            shape="circular"
            checked={status === value}
            onClick={() => {
              setStatus(value);
            }}
          >
            {t(`approvals.tab.filter.${value}`)}
          </ToggleButton>
        ))}
        <ToggleButton
          size="small"
          shape="circular"
          checked={toDecide}
          onClick={() => {
            setToDecide((current) => !current);
          }}
        >
          {t('approvals.tab.filter.toDecide')}
        </ToggleButton>
      </div>

      {flows.isError && !flows.data ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('approvals.tab.loadFailedTitle')}</MessageBarTitle>
            {t('approvals.tab.loadFailedBody')}
          </MessageBarBody>
          <MessageBarActions>
            <Button
              onClick={() => {
                void flows.refetch();
              }}
            >
              {t('common:actions.retry')}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : (
        <DataTable<ApprovalFlow>
          columns={columns}
          data={items}
          getRowId={(row) => row.id}
          loading={flows.isPending}
          caption={t('approvals.tab.caption')}
          empty={
            <EmptyState
              illustration={<DocumentCheckmark24Regular />}
              title={t(`approvals.tab.empty.${emptyKey}.title`)}
              description={t(`approvals.tab.empty.${emptyKey}.description`)}
            />
          }
        />
      )}

      {selected && open?.kind === 'decide' ? (
        <DecisionDialog flow={selected} onClose={close} />
      ) : null}
      {selected && open?.kind === 'details' ? (
        <Dialog
          open
          modalType="modal"
          onOpenChange={(_event, data) => {
            if (!data.open) close();
          }}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                {t('approvals.tab.detailsTitle', {
                  document: isolate(documentLabel(selected.documentCode, selected.documentTitle)),
                })}
              </DialogTitle>
              <DialogContent>
                <FlowCard flow={selected} />
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={close}>
                  {t('common:actions.close')}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      ) : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/approvals-tab.griffel.css";